import React from 'react';

import {createSelector} from '@reduxjs/toolkit';
import {useSelector} from 'react-redux';

import {DashboardPage} from 'app/components/general/DashboardPage';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {ActionButton} from 'app/general/structures/SPPage';
import {useLocalStateFilters} from 'app/hooks';
import {useAccountSettings} from 'app/settings/api';
import {USERS_CREATE_PAGE_URL} from 'app/urls';
import {convertLocationParamsToFetchParams, useUsers} from 'app/user/api';
import {gt, ngt} from 'app/utils/i18n';

import {UserFilterbar} from './UserFilterbar';
import {UserListItem} from './UserListItem';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {
  UsersFetchParamsType,
  UsersLocationParamsType,
} from 'app/user/types';
import type {WithRouterProps} from 'react-router';
import type {
  AccountSettingsSerializedType,
  UserSerializedType,
} from 'sweet-types';

export function UsersDashboard(
  routerProps: WithRouterProps<unknown, UsersLocationParamsType>
) {
  const settings = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: 900,
  }).data;
  const counters = useSelector(managersAndTeammatesCounterSelector(settings));

  return (
    <DashboardPage<
      UserSerializedType,
      UsersLocationParamsType,
      UsersFetchParamsType
    >
      {...routerProps}
      pageTitle={gt('Teammembers')}
      title={gt('Members')}
      subTitle={
        <div className="text-right">
          <div>{counters?.teammates}</div>
          <div>{counters?.managers}</div>
        </div>
      }
      actionArea={({isManager}) =>
        isManager ? (
          <div className="d-flex align-items-center">
            <div className="mr-2">
              <ActionButton
                size="sm"
                text={gt('Add new member')}
                icon="fa-plus-circle"
                link={USERS_CREATE_PAGE_URL}
              />
            </div>
          </div>
        ) : null
      }
      useDataHook={useUsers}
      useFiltersHook={useLocalStateFilters<
        UsersFetchParamsType,
        UsersLocationParamsType
      >(convertLocationParamsToFetchParams)}
      filterbar={({resetFilters, viewingAll}) => (
        <UserFilterbar resetFilters={resetFilters} viewingAll={viewingAll} />
      )}
      renderItem={(user) => (
        <UserListItem key={user.id} user={user} pageContext="dashboard" />
      )}
      messages={{
        counter: (count) => ngt('%s member', '%s members', count),
        notFound: ({viewingAll, resetFilters}, NoResultsMessage) =>
          viewingAll ? (
            <NoResultsMessage
              icon="fa-user"
              head={gt('No members in the account')}
            />
          ) : (
            <NoResultsMessage icon="fa-user" head={gt('No members found')}>
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                {gt('Reset filters and view all members')}
              </button>
            </NoResultsMessage>
          ),
      }}
    />
  );
}

export const UsersDashboardBreadcrumb = (
  props: BreadcrumbProps
): JSX.Element => (
  <BreadcrumbItemStructure icon="fa-user" {...props} text={gt('Members')} />
);

const managersAndTeammatesCounterSelector = (
  settings?: AccountSettingsSerializedType
) =>
  createSelector<
    [() => AccountSettingsSerializedType | undefined],
    | {
        managers: string;
        teammates: string;
      }
    | undefined
  >(
    () => settings,
    (settings) => {
      if (settings == null) {
        return undefined;
      }
      return {
        managers: settings.is_peruser
          ? ngt('%d managers', '%d managers', settings.account_managers)
          : ngt(
              '%d managers of %d',
              '%d managers of %d',
              settings.account_managers,
              settings.no_managers
            ),
        teammates: settings.is_peruser
          ? ngt('%d teammate', '%d teammates', settings.account_teammates)
          : ngt(
              '%d teammate of %d',
              '%d teammates of %d',
              settings.account_teammates,
              settings.no_teammates
            ),
      };
    }
  );
